import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '~/lib/api';

/**
 * Gmail OAuth callback page. Google redirects here with ?code=xxx.
 * We POST the code to the backend for token exchange (stores Gmail refresh token),
 * then redirect to the connect wizard step 1 (NetSuite).
 * Platform auth (accessToken cookie) is already set by OIDC — no cookie handling here.
 */
export default function TbitInvoicesAuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const oauthError = params.get('error');

    if (oauthError) {
      setError(`Error de Google: ${oauthError}`);
      return;
    }

    if (!code) {
      setError('No se recibio un codigo de autorizacion de Google.');
      return;
    }

    (async () => {
      try {
        const res = await apiFetch('/tbit-invoices/auth/exchange', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => null);
          throw new Error(data?.message || data?.error || 'Error al conectar Gmail');
        }

        // Gmail connected — proceed to NetSuite step
        navigate('/tbit-invoices/connect?step=1', { replace: true });
      } catch (err: any) {
        console.error('[tbit-invoices] Auth callback error:', err);
        setError(err.message || 'Error de autenticacion');
      }
    })();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center px-4">
        <div className="card bg-base-100 shadow-lg w-full max-w-md">
          <div className="card-body text-center">
            <h2 className="text-xl font-bold text-error">Error de conexion</h2>
            <p className="text-sm text-base-content/70 mt-2">{error}</p>
            <button
              onClick={() => navigate('/tbit-invoices/connect')}
              className="btn btn-primary mt-4"
            >
              Intentar de nuevo
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200 flex items-center justify-center px-4">
      <div className="card bg-base-100 shadow-lg w-full max-w-md">
        <div className="card-body text-center">
          <span className="loading loading-spinner loading-lg text-primary" />
          <p className="text-sm text-base-content/70 mt-4">Conectando tu cuenta de Gmail...</p>
        </div>
      </div>
    </div>
  );
}
